/**
 * Monday.com API Service — Facade
 *
 * Thin orchestrator that delegates to domain-specific modules:
 * - mondayCore.ts — Infrastructure, GraphQL, column discovery
 * - mondayLeads.ts — Leads CRUD, enrichment, account sync
 * - mondayDeals.ts — Deals, assets, activity logs, users
 * - mondayContacts.ts — Contacts + accounts CRUD, sync
 *
 * Preserves the same public API for all consumers.
 */

import { MondayCore } from './mondayCore';
import type { MondayServiceConfig } from './mondayCore';
import type { Lead, Deal, MondayAsset } from '@/types';

import {
  getLeads,
  getLead,
  findLeadByEmail,
  createLead,
  updateLead,
  deleteLead,
  batchCreateLeads,
  createUpdate,
  createEnrichmentUpdate,
  updateLinkedAccount,
  getLinkedAccountEnrichmentStatus,
} from './mondayLeads';

import {
  getDeals,
  getActivityLogs,
  getAssets,
  getUsers,
} from './mondayDeals';

import {
  getContacts,
  findContactByEmail,
  createContact,
  updateContact,
  syncContactsToMonday,
  findAccountByName,
  createAccount,
  findOrCreateAccount,
  syncCompaniesToMonday,
} from './mondayContacts';

// Re-export types for consumers
export type {
  MondayServiceConfig,
  MondayUser,
  MondayContact,
  MondayActivityLog,
  ParsedActivityData,
  StageTransition,
} from './mondayCore';

class MondayService {
  private core = new MondayCore();

  // ========================================================================
  // Configuration
  // ========================================================================

  configure(config: MondayServiceConfig): void {
    this.core.configure(config);
  }

  // ========================================================================
  // Leads
  // ========================================================================

  async getLeads(pageSize?: number): Promise<Lead[]> {
    return getLeads(this.core, pageSize);
  }

  async getLead(itemId: string): Promise<Lead | null> {
    return getLead(this.core, itemId);
  }

  async findLeadByEmail(email: string): Promise<{ id: string; name: string } | null> {
    return findLeadByEmail(this.core, email);
  }

  async createLead(
    lead: Omit<Lead, 'id' | 'createdAt' | 'lastActivity'>,
    options?: { groupId?: string; listName?: string }
  ): Promise<string> {
    return createLead(this.core, lead, options);
  }

  async updateLead(
    itemId: string,
    updates: Partial<Omit<Lead, 'id'>>
  ): Promise<void> {
    return updateLead(this.core, itemId, updates);
  }

  async deleteLead(itemId: string): Promise<void> {
    return deleteLead(this.core, itemId);
  }

  async batchCreateLeads(
    leads: Array<Omit<Lead, 'id' | 'createdAt' | 'lastActivity'>>,
    options?: { skipDuplicates?: boolean; groupId?: string; listName?: string }
  ): Promise<{ created: string[]; skipped: string[] }> {
    return batchCreateLeads(this.core, leads, options);
  }

  async createUpdate(itemId: string, body: string): Promise<string | null> {
    return createUpdate(this.core, itemId, body);
  }

  async createEnrichmentUpdate(
    itemId: string,
    enrichmentData: {
      industry?: string;
      size?: string;
      location?: string;
      website?: string;
      linkedin?: string;
      phone?: string;
      founded?: number;
      revenue?: string;
    },
    source?: string
  ): Promise<string | null> {
    return createEnrichmentUpdate(this.core, itemId, enrichmentData, source);
  }

  async updateLinkedAccount(
    leadId: string,
    accountData: {
      industry?: string;
      size?: string;
      location?: string;
      website?: string;
      linkedin?: string;
      technologies?: string[];
    },
    leadContext?: {
      company?: string;
      revenue?: string;
      value?: number;
      stage?: string;
      nextAction?: string;
    }
  ): Promise<void> {
    return updateLinkedAccount(this.core, leadId, accountData, leadContext);
  }

  async getLinkedAccountEnrichmentStatus(
    leadIds: string[]
  ): Promise<Map<string, { enriched: boolean; enrichmentDate?: string }>> {
    return getLinkedAccountEnrichmentStatus(this.core, leadIds);
  }

  // ========================================================================
  // Deals & Analytics
  // ========================================================================

  async getDeals(): Promise<Deal[]> {
    return getDeals();
  }

  async getActivityLogs(
    options?: { boardId?: string; limit?: number; pages?: number }
  ) {
    return getActivityLogs(this.core, options);
  }

  // ========================================================================
  // Assets
  // ========================================================================

  async getAssets(): Promise<MondayAsset[]> {
    return getAssets(this.core);
  }

  // ========================================================================
  // Users
  // ========================================================================

  async getUsers() {
    return getUsers(this.core);
  }

  // ========================================================================
  // Contacts & Accounts
  // ========================================================================

  async getContacts() {
    return getContacts(this.core);
  }

  async findContactByEmail(email: string) {
    return findContactByEmail(this.core, email);
  }

  async createContact(
    contact: Parameters<typeof createContact>[1],
    options?: Parameters<typeof createContact>[2]
  ) {
    return createContact(this.core, contact, options);
  }

  async updateContact(
    contactId: string,
    updates: Parameters<typeof updateContact>[2]
  ) {
    return updateContact(this.core, contactId, updates);
  }

  async syncContactsToMonday(
    contacts: Parameters<typeof syncContactsToMonday>[1],
    options?: Parameters<typeof syncContactsToMonday>[2]
  ) {
    return syncContactsToMonday(this.core, contacts, options);
  }

  async findAccountByName(companyName: string) {
    return findAccountByName(this.core, companyName);
  }

  async createAccount(companyName: string, data?: Parameters<typeof createAccount>[2]) {
    return createAccount(this.core, companyName, data);
  }

  async findOrCreateAccount(companyName: string, data?: Parameters<typeof findOrCreateAccount>[2]) {
    return findOrCreateAccount(this.core, companyName, data);
  }

  async syncCompaniesToMonday(
    companies: Parameters<typeof syncCompaniesToMonday>[1],
    options?: Parameters<typeof syncCompaniesToMonday>[2]
  ) {
    return syncCompaniesToMonday(this.core, companies, options);
  }

  // ========================================================================
  // Column Management (delegated to core)
  // ========================================================================

  async getColumnMappingsForBoard(boardId: string, fieldNames: string[]) {
    return this.core.getColumnMappingsForBoard(boardId, fieldNames);
  }

  saveCustomColumnMappings(boardId: string, mappings: Record<string, string>): void {
    this.core.saveCustomColumnMappings(boardId, mappings);
  }

  async getBoard(boardId?: string) {
    return this.core.getBoard(boardId);
  }

  // ========================================================================
  // Cache Management
  // ========================================================================

  clearCache(): void {
    this.core.clearCache();
  }
}

// Singleton instance
export const mondayService = new MondayService();
export default mondayService;
